import React, { useEffect, useMemo, useState } from 'react';

import { FormikValues } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminActivePharmacyList,
  adminAssignedPharmacies,
  adminResetValue,
  assignPharmacies,
  getActivePharmacyList,
  getAssignedPharmacies,
  IAdminPharmacyListBaseData,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import DeactivateDialog from '../../../components/Admin/PharmacistList/DeactivateDialog';
import useBidirectionalTransferList, {
  IInput,
} from '../../useBidirectionalTransferList';
import { DeactivateModalIcon } from '../../../helpers/Constants';
import { setDialog } from '../../../reducers/dialogReducer';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import strings from '../../../localization';

const successAction = adminActions.assignPharmacies;

const useAssignAccounts = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { userId, userName } = state ?? {};

  const [search, setSearch] = useState('');

  const allPharmacies = useAppSelector(adminActivePharmacyList);
  const assignedPharmacies = useAppSelector(adminAssignedPharmacies);
  const assignSuccess = useAppSelector((st) =>
    successSelector([successAction], st));

  const { data } = allPharmacies ?? {};

  // The endpoint returns ids only, so the names and emails on the right come
  // from the same pharmacy list that feeds the left.
  const assignedAccounts = useMemo(
    () =>
      data.filter((item: IAdminPharmacyListBaseData) =>
        assignedPharmacies.includes(item.userId)),
    [data, assignedPharmacies],
  );

  const searchTerm = search.trim().toLowerCase();

  // An account owned by a different admin is off limits here. The whole list
  // is already loaded, so the search narrows it in place.
  const selectablePharmacies = useMemo(
    () =>
      data.filter((item: IAdminPharmacyListBaseData) => {
        const { ownerAdminId, name, email } = item ?? {};

        if (ownerAdminId && ownerAdminId !== userId) {
          return false;
        }

        return (
          !searchTerm
          || name?.toLowerCase().includes(searchTerm)
          || email?.toLowerCase().includes(searchTerm)
        );
      }),
    [data, userId, searchTerm],
  );

  const leftInput: IInput<IAdminPharmacyListBaseData> = useMemo(
    () =>
      ({
        data: selectablePharmacies,
        idKey: 'userId',
        nameKey: 'name',
        emailKey: 'email',
      }),
    [selectablePharmacies],
  );

  const rightInput: IInput<IAdminPharmacyListBaseData> = useMemo(
    () =>
      ({
        data: assignedAccounts,
        idKey: 'userId',
        nameKey: 'name',
        emailKey: 'email',
      }),
    [assignedAccounts],
  );

  const { list, setList, left, right } = useBidirectionalTransferList({
    leftInput,
    rightInput,
  });

  const pharmacyUserIds = useMemo(
    () =>
      list.right.map((item) =>
        item.key),
    [list.right],
  );

  const handleAssign = (values: FormikValues) => {
    const { reason } = values;
    dispatch(assignPharmacies({ userId, pharmacyUserIds, notes: reason }));
  };

  const showConfirmation = () => {
    dispatch(
      setDialog({
        showCloseButton: true,
        heading: {
          title: '',
        },
        Component: (
          <DeactivateDialog
            id={userId}
            iconType={DeactivateModalIcon.positiveConfirmation}
            heading={strings.confirmAssignment}
            confirmationMessage={
              strings.formatString(
                strings.areYouSureAssignAccounts,
                `${pharmacyUserIds.length}`,
                userName,
              ) as string
            }
            initialValues={{ id: userId, reason: '' }}
            validator={null}
            primaryButtonLabel={strings.assign}
            onSubmit={handleAssign}
          />
        ),
      }),
    );
  };

  useEffect(() => {
    dispatch(getActivePharmacyList({ page: 0, itemPerPage: 0 }));
    if (userId) {
      dispatch(getAssignedPharmacies(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (assignSuccess) {
      dispatch(resetStatus([successAction]));
      showSuccess(dispatch, strings.accountsAssignedSuccessfully);
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignSuccess]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue(['assignedPharmacies', 'activePharmacyList']));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { list, setList, left, right, userName, showConfirmation, setSearch };
};

export default useAssignAccounts;
